import { formatTime, minutesToTime } from 'shared/lib/time';
import { selectOrderForBooking, updateBookingDate } from 'entities/orders';
import { useAppDispatch, useAppSelector } from 'shared/lib/store';
import { useEffect, useRef, useState } from 'react';
import { Option } from 'shared/types';
import {
	BookingTime,
	selectSelectedTimeslot,
	selectTimesOptions,
	useCurrentTimeslot,
	useSelectedTimeslot,
	useTimeslotZone,
} from 'entities/toolbar/timeslots';
import { selectSettings } from 'entities/settings/model/selector';
import { OrderBookingData } from 'entities/orders/model/types';
import { localStorageItem } from 'shared/lib/core/lib/utils';
import { useBookingReset } from 'features/booking/BookingClientForm';

export const useOrderOptions = (
	timeslotDate: string,
	setDate: React.Dispatch<React.SetStateAction<string>>,
	setDurationOption: (option: Option) => void,
	setDurationByTime: (timeString: string) => void,
	activeBookingTime: BookingTime | null
) => {
	const order = useAppSelector(selectOrderForBooking);
	const selectedTimeslot = useAppSelector(selectSelectedTimeslot);
	const setting = useAppSelector(selectSettings);
	const { time } = useSelectedTimeslot(selectedTimeslot);
	const timesOptions = useAppSelector(selectTimesOptions);
	const { toCurrentTimeslot } = useCurrentTimeslot()

	const [dateOption, setDateOption] = useState(!order ? timeslotDate : '');
	const [timeOption, setTimeOption] = useState<Option | null>(null);
	const savedData = localStorageItem('booking') as OrderBookingData;
	const { formatISODateBySlotZone } = useTimeslotZone();

	const [guestsOption, setGuestsOption] = useState(
		!order ? { value: setting?.default_participants_count?.toString() || '1', label: setting?.default_participants_count?.toString() || '1' } : null,
	);

	const dataIsLoaded = useRef(false);
	const dispatch = useAppDispatch();

	useEffect(() => {
		if (!dataIsLoaded.current) {
			if (order) {
				const dateTime = order?.dt_start;
				const formattedTime = formatTime({
					time: dateTime,
					byUtc: true,
				});
				setDateOption(dateTime);

				if (formattedTime) {
					const option = {
						label: formattedTime.time,
						value: formattedTime.time,
					}
					setTimeOption(option);
					localStorage.setItem('favoriteSavedTime', JSON.stringify(option));
					const duration = order.duration;
					const durationTime = minutesToTime(duration);
					setDurationOption({ value: durationTime, label: durationTime });
					dispatch(updateBookingDate({ date: dateTime }));
				}

				const guests = order.participants_count;
				setGuestsOption({
					label: guests.toString(),
					value: guests.toString(),
				});

				dataIsLoaded.current = true;

				return;
			}

			if (savedData) {
				const dateTime = savedData?.dt_start;
				const formattedTime = formatTime({
					time: dateTime,
					byUtc: true,
				});
				setDateOption(dateTime);

				if (formattedTime) {
					const favoriteSavedTime = localStorageItem('favoriteSavedTime');
						
					if (favoriteSavedTime) {
						setTimeOption(favoriteSavedTime);
					} else {
						const option = {
							label: formattedTime.time,
							value: formattedTime.time,
						}
						setTimeOption(option);
						localStorage.setItem('favoriteSavedTime', JSON.stringify(option));
					}

					const duration = savedData.duration;
					const durationTime = minutesToTime(duration);
					setDurationOption({ value: durationTime, label: durationTime });
					dispatch(updateBookingDate({ date: dateTime }));
				}

				const guests = savedData.participants_count;
				setGuestsOption({
					label: guests.toString(),
					value: guests.toString(),
				});

				dataIsLoaded.current = true;
			}

      if (activeBookingTime&&timeOption?.value !== activeBookingTime.time) {
        const option = {
          label: activeBookingTime.time,
          value: activeBookingTime.time,
        }
				setTimeOption(option);
      }
		}
	// eslint-disable-next-line
	}, [order, timesOptions, dispatch, activeBookingTime, setDurationOption]);

	const optionBySelectedSlotIsLoaded = useRef(false);

	useBookingReset(async () => {
		await toCurrentTimeslot();
		setGuestsOption({ value: setting?.default_participants_count?.toString() || '1', label: setting?.default_participants_count?.toString() || '1' })
		setDurationByTime(time);
		setTimeOption({
			label: time,
			value: time,
		});
		setDate(selectedTimeslot);
		setDateOption(formatISODateBySlotZone(new Date()))
	})

	useEffect(() => {
		if (!order && !optionBySelectedSlotIsLoaded.current && !savedData) {
			if (selectedTimeslot) {
				const option = {
					label: time,
					value: time,
				}
				setTimeOption(option);
				localStorage.setItem('favoriteSavedTime', JSON.stringify(option));

				setDurationByTime(time);

				dispatch(
					updateBookingDate({ date: selectedTimeslot }),
				);

				setDate(selectedTimeslot);
			} else if (timesOptions.length) {
				setTimeOption(timesOptions[0]);
				localStorage.setItem('favoriteSavedTime', JSON.stringify(timesOptions[0]));
			}

		}
		optionBySelectedSlotIsLoaded.current = true;
		// eslint-disable-next-line
	}, [selectedTimeslot, time, order, timesOptions, dispatch, setDate, setDurationByTime]);

	return {
		dateOption,
		timeOption,
		guestsOption,
	};
};
